// apps/web/src/views/app-views/roulette/client-users/index.js

import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import {
  CheckSquareOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { useEffect, useMemo, useState } from 'react'

import {
  createClientUser,
  deleteClientUser,
  getClientUsers,
  updateClientUser,
} from 'services/clientUsersService'
import { getClients } from 'services/clientsService'
import { api } from 'services/api'

import {
  ALL_PERMISSIONS,
  PERMISSION_GROUPS,
  getDefaultPermissionsByClientRole,
} from 'constants/permissions'

const { Title, Text } = Typography
const { Option } = Select

const ClientUsersPage = () => {
  const [users, setUsers] = useState([])
  const [clients, setClients] = useState([])
  const [games, setGames] = useState([])
  const [loadingGames, setLoadingGames] = useState(false)
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [form] = Form.useForm()

  const userRole = localStorage.getItem('USER_ROLE')
  const isAdminNlb = userRole === 'ADMIN_NLB'

  const selectedPermissions = Form.useWatch(
    'permissions',
    form,
  ) || []

  const selectedClientId = Form.useWatch(
  'clientId',
  form,
)

  const selectedClientRole = Form.useWatch(
    'clientRole',
    form,
)

  const allPermissionsSelected = useMemo(() => {
    return (
      selectedPermissions.length ===
      ALL_PERMISSIONS.length
    )
  }, [selectedPermissions])

  const somePermissionsSelected = useMemo(() => {
    return (
      selectedPermissions.length > 0 &&
      selectedPermissions.length <
        ALL_PERMISSIONS.length
    )
  }, [selectedPermissions])

  const loadClients = async () => {
    if (!isAdminNlb) {
      return
    }

    try {
      const data = await getClients()

      setClients(
        Array.isArray(data)
          ? data
          : [],
      )
    } catch (error) {
      console.error(
        'ERRO AO CARREGAR CLIENTES:',
        error,
      )

      message.error(
        'Erro ao carregar clientes',
      )

      setClients([])
    }
  }

  const loadUsers = async () => {
    setLoading(true)

    try {
      const clientId = isAdminNlb
        ? undefined
        : localStorage.getItem(
            'CLIENT_ID',
          )

      const data =
        await getClientUsers(
          clientId,
        )

      setUsers(
        Array.isArray(data)
          ? data
          : [],
      )
    } catch (error) {
      console.error(
        'ERRO CLIENT USERS:',
        error,
      )

      message.error(
        error?.response?.data?.message ||
          'Erro ao carregar usuários',
      )

      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  const openCreateModal = () => {
  setEditingUser(null)
  setGames([])
  form.resetFields()

  const defaultClientRole = 'OPERATOR'
  const currentClientId = isAdminNlb
    ? undefined
    : localStorage.getItem('CLIENT_ID')

  form.setFieldsValue({
    clientId: currentClientId,
    role: 'OPERATOR',
    clientRole: defaultClientRole,
    isActive: true,
    gameIds: [],
    permissions:
      getDefaultPermissionsByClientRole(
        defaultClientRole,
      ),
  })

  setModalOpen(true)
  loadClients()

  if (currentClientId) {
    loadGames(currentClientId)
  }
}

  const openEditModal = async (record) => {
  setEditingUser(record)

  const recordPermissions =
    Array.isArray(record.permissions)
      ? record.permissions
      : getDefaultPermissionsByClientRole(
          record.role,
        )

  const recordGameIds =
    Array.isArray(record.gameIds)
      ? record.gameIds
      : Array.isArray(record.games)
        ? record.games.map((game) => game.id)
        : Array.isArray(record.gameAccesses)
          ? record.gameAccesses
              .map(
                (access) =>
                  access.gameId ||
                  access.game?.id,
              )
              .filter(Boolean)
          : []

  form.resetFields()

  form.setFieldsValue({
    clientId: record.clientId,
    name: record.user?.name,
    email: record.user?.email,
    role: record.user?.role,
    clientRole: record.role,
    isActive: record.user?.isActive,
    password: '',
    permissions: recordPermissions,
    gameIds: recordGameIds,
  })

  setModalOpen(true)
  await loadClients()
  await loadGames(record.clientId)
}

  const closeModal = () => {
    setModalOpen(false)
    setEditingUser(null)
    form.resetFields()
  }

  const handleClientRoleChange = (
  clientRole,
) => {
  if (clientRole === 'ADMIN') {
    form.setFieldsValue({
      gameIds: [],
    })
  }

  Modal.confirm({
    title: 'Aplicar permissões padrão?',
    content:
      'Deseja substituir as permissões atuais pelas permissões padrão deste perfil?',
    okText: 'Aplicar padrões',
    cancelText: 'Manter atuais',

    onOk: () => {
      form.setFieldsValue({
        permissions:
          getDefaultPermissionsByClientRole(
            clientRole,
          ),
      })
    },
  })
}

  const handleToggleAllPermissions = (
    event,
  ) => {
    form.setFieldsValue({
      permissions:
        event.target.checked
          ? [...ALL_PERMISSIONS]
          : [],
    })
  }

  const handleTogglePermissionGroup = (
    group,
    checked,
  ) => {
    const currentPermissions =
      Array.isArray(
        form.getFieldValue(
          'permissions',
        ),
      )
        ? form.getFieldValue(
            'permissions',
          )
        : []

    const groupPermissionValues =
      group.permissions.map(
        (permission) =>
          permission.value,
      )

    if (checked) {
      form.setFieldsValue({
        permissions: [
          ...new Set([
            ...currentPermissions,
            ...groupPermissionValues,
          ]),
        ],
      })

      return
    }

    form.setFieldsValue({
      permissions:
        currentPermissions.filter(
          (permission) =>
            !groupPermissionValues.includes(
              permission,
            ),
        ),
    })
  }

  const handleSubmit = async (
    values,
  ) => {
    try {
      const permissions =
        Array.isArray(
          values.permissions,
        )
          ? values.permissions
          : []

      const payload = {
  name: values.name,
  email: values.email,
  role: values.role,
  clientRole: values.clientRole,
  isActive: values.isActive,
  permissions,

  gameIds: Array.isArray(values.gameIds)
    ? values.gameIds
    : [],
}

      if (
        isAdminNlb &&
        values.clientId
      ) {
        payload.clientId =
          values.clientId
      }

      if (values.password) {
        payload.password =
          values.password
      }

      if (editingUser) {
        await updateClientUser(
          editingUser.id,
          payload,
        )

        message.success(
          'Usuário atualizado',
        )
      } else {
        await createClientUser({
          ...payload,
          password:
            values.password,
        })

        message.success(
          'Usuário criado',
        )
      }

      closeModal()
      await loadUsers()
    } catch (error) {
      console.error(
        'ERRO SAVE CLIENT USER:',
        error?.response?.data ||
          error,
      )

      message.error(
        error?.response?.data?.message ||
          'Erro ao salvar usuário',
      )
    }
  }

  const handleDelete = async (
    record,
  ) => {
    try {
      await deleteClientUser(
        record.id,
      )

      message.success(
        'Usuário removido',
      )

      await loadUsers()
    } catch (error) {
      console.error(
        'ERRO DELETE CLIENT USER:',
        error?.response?.data ||
          error,
      )

      message.error(
        error?.response?.data?.message ||
          'Erro ao remover usuário',
      )
    }
  }

  useEffect(() => {
    loadUsers()
    loadClients()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadGames = async (clientId) => {
  const resolvedClientId =
    clientId ||
    localStorage.getItem('CLIENT_ID')

  if (!resolvedClientId) {
    setGames([])
    return
  }

  setLoadingGames(true)

  try {
    const response = await api.get('/games')
    const data = response?.data || response
    const list = Array.isArray(data) ? data : []

    const filteredGames = isAdminNlb
      ? list.filter((game) => {
          const gameClientId =
            game.event?.clientId ||
            game.event?.client?.id

          return gameClientId === resolvedClientId
        })
      : list

    setGames(
      filteredGames
        .slice()
        .sort((a, b) => {
          const eventA = a.event?.name || ''
          const eventB = b.event?.name || ''

          if (eventA !== eventB) {
            return eventA.localeCompare(eventB)
          }

          return String(a.name || '').localeCompare(
            String(b.name || ''),
          )
        }),
    )
  } catch (error) {
    console.error(
      'ERRO AO CARREGAR JOGOS:',
      error,
    )

    message.error(
      error?.response?.data?.message ||
        'Erro ao carregar jogos',
    )

    setGames([])
  } finally {
    setLoadingGames(false)
  }
}

  return (
    <>
      <Space
        style={{
          width: '100%',
          justifyContent:
            'space-between',
          marginBottom: 16,
        }}
      >
        <div>
          <Title
            level={2}
            style={{ margin: 0 }}
          >
            {isAdminNlb
              ? 'Usuários de Clientes'
              : 'Usuários'}
          </Title>

          <p>
            Gerencie acessos,
            operadores e permissões
            da plataforma.
          </p>
        </div>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={
            openCreateModal
          }
        >
          Novo Usuário
        </Button>
      </Space>

      <Card>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={users}
          scroll={{ x: 1450 }}
          columns={[
            {
              title: 'Nome',
              width: 190,
              fixed: 'left',

              render: (_, record) =>
                record.user?.name ||
                '-',
            },
            {
              title: 'E-mail',
              width: 240,

              render: (_, record) =>
                record.user?.email ||
                '-',
            },
            {
              title: 'Cliente',
              width: 180,

              render: (_, record) =>
                record.client?.name ||
                '-',
            },
            {
              title:
                'Perfil global',
              width: 150,

              render: (_, record) => (
                <Tag
                  color={
                    record.user
                      ?.role ===
                    'CLIENT_ADMIN'
                      ? 'blue'
                      : 'default'
                  }
                >
                  {record.user
                    ?.role || '-'}
                </Tag>
              ),
            },
            {
              title:
                'Perfil no cliente',
              dataIndex: 'role',
              width: 150,

              render: (role) => (
                <Tag
                  color={
                    role === 'ADMIN'
                      ? 'green'
                      : role ===
                          'OPERATOR'
                        ? 'orange'
                        : 'default'
                  }
                >
                  {role}
                </Tag>
              ),
            },
              {
  title: 'Jogos',
  width: 220,

  render: (_, record) => {
    if (record.role === 'ADMIN') {
      return (
        <Tag color="green">
          Todos do cliente
        </Tag>
      )
    }

    const recordGames =
      Array.isArray(record.games)
        ? record.games
        : []

    if (recordGames.length === 0) {
      return (
        <Tag color="red">
          Nenhum jogo
        </Tag>
      )
    }

    return (
      <Space
        direction="vertical"
        size={4}
      >
        {recordGames
          .slice(0, 3)
          .map((game) => (
            <Tag key={game.id} color="blue">
              {game.event?.name
                ? `${game.event.name} — `
                : ''}
              {game.name}
            </Tag>
          ))}

        {recordGames.length > 3 && (
          <Text type="secondary">
            +{recordGames.length - 3} jogos
          </Text>
        )}
      </Space>
    )
  },
},
            {
              title: 'Permissões',
              width: 170,

              render: (_, record) => {
                const permissions =
                  Array.isArray(
                    record.permissions,
                  )
                    ? record.permissions
                    : []

                if (
                  permissions.length ===
                  ALL_PERMISSIONS.length
                ) {
                  return (
                    <Tag
                      color="green"
                      icon={
                        <CheckSquareOutlined />
                      }
                    >
                      Acesso total
                    </Tag>
                  )
                }

                if (
                  permissions.length ===
                  0
                ) {
                  return (
                    <Tag color="red">
                      Sem acesso
                    </Tag>
                  )
                }

                return (
                  <Tag color="blue">
                    {
                      permissions.length
                    }{' '}
                    permissões
                  </Tag>
                )
              },
            },
            {
              title: 'Status',
              width: 110,

              render: (_, record) => (
                <Tag
                  color={
                    record.user
                      ?.isActive
                      ? 'green'
                      : 'default'
                  }
                >
                  {record.user
                    ?.isActive
                    ? 'Ativo'
                    : 'Inativo'}
                </Tag>
              ),
            },
            {
              title: 'Ações',
              width: 240,
              fixed: 'right',

              render: (_, record) => (
                <Space>
                  <Button
                    icon={
                      <EditOutlined />
                    }
                    onClick={() =>
                      openEditModal(
                        record,
                      )
                    }
                  >
                    Editar
                  </Button>

                  <Popconfirm
                    title="Deseja remover este usuário?"
                    okText="Sim"
                    cancelText="Não"
                    onConfirm={() =>
                      handleDelete(
                        record,
                      )
                    }
                  >
                    <Button
                      danger
                      icon={
                        <DeleteOutlined />
                      }
                    >
                      Remover
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={
          editingUser
            ? 'Editar Usuário'
            : 'Novo Usuário'
        }
        visible={modalOpen}
        onCancel={closeModal}
        onOk={() =>
          form.submit()
        }
        okText="Salvar"
        cancelText="Cancelar"
        width={900}
        destroyOnClose
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
        >
          {isAdminNlb && (
            <Form.Item
  name="clientId"
  label="Cliente"
  rules={[
    {
      required: true,
      message: 'Selecione o cliente',
    },
  ]}
>
  <Select
    showSearch
    placeholder="Selecione o cliente"
    optionFilterProp="children"
    onChange={(clientId) => {
      form.setFieldsValue({
        gameIds: [],
      })

      loadGames(clientId)
    }}
    filterOption={(input, option) =>
      String(option?.children || '')
        .toLowerCase()
        .includes(input.toLowerCase())
    }
  >
    {clients.map((client) => (
      <Option
        key={client.id}
        value={client.id}
      >
        {client.name}
      </Option>
    ))}
  </Select>
</Form.Item>
          )}

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Nome"
                rules={[
                  {
                    required: true,
                    message:
                      'Informe o nome',
                  },
                ]}
              >
                <Input placeholder="Nome do usuário" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="email"
                label="E-mail"
                rules={[
                  {
                    required: true,
                    type: 'email',
                    message:
                      'Informe um e-mail válido',
                  },
                ]}
              >
                <Input placeholder="usuario@email.com" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              {isAdminNlb ? (
                <Form.Item
                  name="role"
                  label="Perfil global"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select>
                    <Option value="CLIENT_ADMIN">
                      CLIENT_ADMIN
                    </Option>

                    <Option value="OPERATOR">
                      OPERATOR
                    </Option>
                  </Select>
                </Form.Item>
              ) : (
                <Form.Item
                  name="role"
                  initialValue="OPERATOR"
                  hidden
                >
                  <Input />
                </Form.Item>
              )}
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="clientRole"
                label="Perfil no cliente"
                rules={[
                  {
                    required: true,
                    message:
                      'Selecione o perfil',
                  },
                ]}
              >
                <Select
                  onChange={
                    handleClientRoleChange
                  }
                >
                  {isAdminNlb && (
                    <Option value="ADMIN">
                      ADMIN
                    </Option>
                  )}

                  <Option value="OPERATOR">
                    OPERATOR
                  </Option>

                  <Option value="VIEWER">
                    VIEWER
                  </Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="isActive"
                label="Status"
                initialValue={true}
              >
                <Select>
                  <Option value={true}>
                    Ativo
                  </Option>

                  <Option value={false}>
                    Inativo
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="password"
            label={
              editingUser
                ? 'Nova senha'
                : 'Senha'
            }
            rules={[
              {
                required:
                  !editingUser,

                message:
                  'Informe uma senha',
              },
              {
                min: 6,
                message:
                  'A senha deve ter no mínimo 6 caracteres',
              },
            ]}
          >
            <Input.Password
              placeholder={
                editingUser
                  ? 'Deixe em branco para manter a senha atual'
                  : 'Senha de acesso'
              }
            />
          </Form.Item>

              <Divider orientation="left">
  Jogos permitidos
</Divider>

<Alert
  type="info"
  showIcon
  style={{ marginBottom: 16 }}
  message={
    selectedClientRole === 'ADMIN'
      ? 'O administrador do cliente terá acesso a todos os jogos do cliente.'
      : 'Selecione exatamente quais jogos este usuário poderá visualizar e operar.'
  }
/>

<Form.Item
  name="gameIds"
  label="Jogos que o usuário poderá acessar"
  rules={[
    {
      validator: (_, value) => {
        if (selectedClientRole === 'ADMIN') {
          return Promise.resolve()
        }

        if (
          !Array.isArray(value) ||
          value.length === 0
        ) {
          return Promise.reject(
            new Error(
              'Selecione pelo menos um jogo',
            ),
          )
        }

        return Promise.resolve()
      },
    },
  ]}
>
  <Select
    mode="multiple"
    allowClear
    showSearch
    loading={loadingGames}
    disabled={
      selectedClientRole === 'ADMIN' ||
      (isAdminNlb && !selectedClientId)
    }
    placeholder={
      selectedClientRole === 'ADMIN'
        ? 'Acesso automático a todos os jogos'
        : 'Selecione um ou mais jogos'
    }
    optionFilterProp="label"
    options={games.map((game) => ({
      value: game.id,

      label: `${
        game.event?.name || 'Evento'
      } — ${game.name}`,
    }))}
  />
</Form.Item>

{selectedClientRole !== 'ADMIN' &&
  games.length === 0 &&
  !loadingGames && (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 16 }}
      message="Nenhum jogo foi encontrado para o cliente selecionado."
    />
  )}

          <Divider orientation="left">
            Permissões de acesso
          </Divider>

          <Alert
            type="info"
            showIcon
            style={{
              marginBottom: 16,
            }}
            message="Defina exatamente quais páginas e operações este usuário poderá acessar."
          />

          <div
            style={{
              marginBottom: 16,
            }}
          >
            <Checkbox
              checked={
                allPermissionsSelected
              }
              indeterminate={
                somePermissionsSelected
              }
              onChange={
                handleToggleAllPermissions
              }
            >
              <Text strong>
                Selecionar todas as
                permissões
              </Text>
            </Checkbox>
          </div>

          <Form.Item
            name="permissions"
            rules={[
              {
                validator: (
                  _,
                  value,
                ) => {
                  if (
                    !Array.isArray(
                      value,
                    ) ||
                    value.length === 0
                  ) {
                    return Promise.reject(
                      new Error(
                        'Selecione pelo menos uma permissão',
                      ),
                    )
                  }

                  return Promise.resolve()
                },
              },
            ]}
          >
            <Checkbox.Group
              style={{
                width: '100%',
              }}
            >
              <Row gutter={[16, 16]}>
                {PERMISSION_GROUPS.map(
                  (group) => {
                    const groupValues =
                      group.permissions.map(
                        (permission) =>
                          permission.value,
                      )

                    const selectedInGroup =
                      groupValues.filter(
                        (permission) =>
                          selectedPermissions.includes(
                            permission,
                          ),
                      )

                    const groupChecked =
                      selectedInGroup.length ===
                      groupValues.length

                    const groupIndeterminate =
                      selectedInGroup.length >
                        0 &&
                      selectedInGroup.length <
                        groupValues.length

                    return (
                      <Col
                        xs={24}
                        md={12}
                        key={group.key}
                      >
                        <Card
                          size="small"
                          title={
                            <Checkbox
                              checked={
                                groupChecked
                              }
                              indeterminate={
                                groupIndeterminate
                              }
                              onChange={(
                                event,
                              ) =>
                                handleTogglePermissionGroup(
                                  group,
                                  event
                                    .target
                                    .checked,
                                )
                              }
                            >
                              <Text strong>
                                {
                                  group.title
                                }
                              </Text>
                            </Checkbox>
                          }
                          style={{
                            height: '100%',
                          }}
                        >
                          <Space
                            direction="vertical"
                            style={{
                              width: '100%',
                            }}
                          >
                            {group.permissions.map(
                              (
                                permission,
                              ) => (
                                <Checkbox
                                  key={
                                    permission.value
                                  }
                                  value={
                                    permission.value
                                  }
                                >
                                  {
                                    permission.label
                                  }
                                </Checkbox>
                              ),
                            )}
                          </Space>
                        </Card>
                      </Col>
                    )
                  },
                )}
              </Row>
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default ClientUsersPage